import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal/Modal.js';
import './RecaptchaV2Modal.css';

const RecaptchaV2Modal = ({ 
  isOpen, 
  onClose, 
  onVerified, 
  onError,
  siteKey 
}) => {
  const recaptchaRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    // Load reCAPTCHA v2 script if not already loaded
    const loadRecaptchaV2 = () => {
      return new Promise((resolve, reject) => {
        // Check if reCAPTCHA v2 is already loaded
        if (window.grecaptcha && window.grecaptcha.render) {
          resolve();
          return;
        }

        // Create script element for reCAPTCHA v2
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaV2Load&render=explicit';
        script.async = true;
        script.defer = true;

        // Set up global callback
        window.onRecaptchaV2Load = () => {
          resolve();
        };

        script.onerror = () => {
          reject(new Error('Failed to load reCAPTCHA v2 script'));
        };

        document.head.appendChild(script);
      });
    };

    const initializeRecaptcha = async () => {
      try {
        setIsLoading(true);
        setError(null);

        await loadRecaptchaV2();

        if (recaptchaRef.current && window.grecaptcha) {
          // Clear any existing reCAPTCHA
          recaptchaRef.current.innerHTML = '';

          // Check if site key is available
          if (!siteKey) {
            setError('Errore di configurazione. Contatta il supporto.');
            setIsLoading(false);
            return;
          }

          // Render reCAPTCHA v2
          window.grecaptcha.render(recaptchaRef.current, {
            sitekey: siteKey,
            callback: (token) => {
              if (window.grecaptcha && window.grecaptcha.reset) {
                try {
                  window.grecaptcha.reset();
                } catch (err) {
                  // Silent fail
                }
              }
              onVerified(token);
            },
            'error-callback': () => {
              setError('Verifica fallita. Riprova.');
            },
            'expired-callback': () => {
              setError('Verifica scaduta. Riprova.');
            }
          });
        }

        setIsLoading(false);
      } catch (err) {
        setError('Errore di caricamento. Ricarica la pagina.');
        setIsLoading(false);
        onError(err.message);
      }
    };

    initializeRecaptcha();

    // Cleanup function - capture ref.current to avoid stale ref
    const containerRef = recaptchaRef.current;
    return () => {
      if (containerRef) {
        containerRef.innerHTML = '';
      }
    };
  }, [isOpen, siteKey, onVerified, onError]);

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    
    // Reset and re-render reCAPTCHA
    if (window.grecaptcha && recaptchaRef.current) {
      try {
        window.grecaptcha.reset();
        recaptchaRef.current.innerHTML = '';
        
        window.grecaptcha.render(recaptchaRef.current, {
          sitekey: siteKey,
          callback: (token) => {
            if (window.grecaptcha && window.grecaptcha.reset) {
              try {
                window.grecaptcha.reset();
              } catch (err) {
                // Silent fail
              }
            }
            onVerified(token);
          },
          'error-callback': () => {
            setError('Verifica fallita. Riprova.');
            onError('reCAPTCHA v2 verification failed');
          },
          'expired-callback': () => {
            setError('Verifica scaduta. Riprova.');
          }
        });
        setIsLoading(false);
      } catch (err) {
        setError('Errore durante il tentativo. Ricarica la pagina.');
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} className="recaptcha-v2-modal" preventClose={true}>
      <div className="recaptcha-v2-content">
        <div className="recaptcha-v2-header">
          <h3>Verifica di Sicurezza</h3>
        </div>

        <div className="recaptcha-v2-body">
          {isLoading && (
            <div className="recaptcha-loading">
              <div className="loading-spinner"></div>
              <p>Caricamento verifica...</p>
            </div>
          )}

          {error && (
            <div className="recaptcha-error">
              <p className="error-message">{error}</p>
              <button 
                className="retry-button" 
                onClick={handleRetry}
                type="button"
              >
                Riprova
              </button>
            </div>
          )}

          <div 
            ref={recaptchaRef} 
            className="recaptcha-container"
            style={{ display: isLoading ? 'none' : 'block' }}
          ></div>
        </div>

     
      </div>
    </Modal>
  );
};

export default RecaptchaV2Modal;
